/**
 * 合同电子版收件：上传 → 自动提取 → 预填表单
 *
 * 【为什么抽成组件而不是在两处各写一遍】
 * 「登记合同」与「新增合同」要做的是同一件事。这段逻辑里有几条**不明显的规则**，
 * 抄两份迟早会分叉：
 *
 *  1. **只预填用户还没动过的字段**（`isFieldTouched`）。用户先填了金额再上传附件，
 *     识别结果不该把他的手工值冲掉。
 *  2. **低置信度或带冲突的候选不自动填**，只在面板上作为"建议"展示。
 *     识别错了却自动填进去，比让人多填一次危险得多。
 *  3. **两个名称候选（合同标题 / 工程名称）都不自动填**。同一份合同里
 *     「商品混凝土买卖合同」与「泰安市…施工工程」是两个不同的东西，
 *     而表单只有一个"合同名称"。替用户二选一，选错了他还看不出来。
 *  4. **`applied` 的键必须与面板行的 `key` 一致**（都是英文候选名）。
 *     从前用中文标签去查，查出来恒为 undefined，于是**输入框明明已被自动填好、
 *     文案却写「未预填」**——用户看得见框里有值，文字却说没预填。
 *     这条有专门的回归测试：`e2e/verify-contract-prefill-panel.mjs`。
 *
 * ⚠ **面板的 DOM 与文案是被测过的契约**，重构时别动：
 *   * `e2e/verify-contract-prefill-panel.mjs` 靠 `由附件自动识别` 这句找面板，
 *     靠 `.ant-alert-description .ant-space-item` 找候选行。
 *   * 我在抽出本组件时顺手把文案改成「由合同电子版自动识别」，那条测试**立刻超时**
 *     ——它等的是一个不再存在的字符串。改文案就必须同步改测试，且要想清楚
 *     值不值得：用户对 LegalWorkbench 上那句话的认知也是资产。
 * 因此这里保留原措辞；「合同电子版」这个说法只出现在**上传区的提示**里。
 */

import { forwardRef, useImperativeHandle, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import type { FormInstance } from 'antd';
import { FileUploader } from '@/components/FileUploader';
import { AttachmentStatusTag } from '@/components/AttachmentStatusTag';
import { contractApi } from '@/api/endpoints';
import type { ContractAttachment, ExtractedCandidates } from '@ilc/shared';

/** 面板一行对应哪个候选、以及它在表单里落到哪个字段 */
interface CandidateRow {
  key: keyof ExtractedCandidates;
  label: string;
  /** 补充说明，展示在"依据"后面 */
  hint?: string;
  /** 没有独立表单字段的候选（两个名称候选共用"合同名称"），由 adopt 特别处理 */
}

const CANDIDATE_ROWS: CandidateRow[] = [
  {
    key: 'contractTitle',
    label: '合同名称（标题）',
    hint: '与下面的工程名称可能不同，请按台账习惯二选一',
  },
  { key: 'projectName', label: '工程名称' },
  { key: 'amountCents', label: '合同金额' },
  // 默认与法务工作台的表单字段同名；合同台账用 labelOverrides 改成「对方单位」
  { key: 'supplierName', label: '供应商' },
  { key: 'supplierTaxNo', label: '供应商税号' },
  { key: 'signDate', label: '签订日期' },
  { key: 'categories', label: '约定品类' },
];

export interface ContractAttachmentIntakeProps {
  /** 表单实例：预填要读 `isFieldTouched`、写 `setFieldsValue` */
  form: FormInstance;
  /**
   * 已有合同 ID。
   *
   * 传了 = 往**已有合同**上传：只刷新识别面板，**绝不改写表单里的值**——
   * 那个表单里是这份合同的现有数据，被识别结果盖掉不是"预填"而是"篡改"。
   * 不传 = **新建时先传附件**：附件落在 TEMP 态，通过 `onAttachmentIdsChange`
   * 把 ID 交给调用方，创建合同时一并绑定。
   */
  contractId?: string | null;
  /** TEMP 态附件 ID 变化时回调（新建场景用） */
  onAttachmentIdsChange?: (ids: string[]) => void;
  /** 上传并解析完成后回调，调用方据此刷新它自己的列表或详情 */
  onChanged?: (attachment: ContractAttachment) => void;
  /** 是否渲染内置的上传件列表。调用方自己有更丰富的列表（含原件/表格预览）时传 false */
  showList?: boolean;
  disabled?: boolean;
  /** 上传区下方的补充说明 */
  hint?: React.ReactNode;
  /**
   * 面板行的显示名，按调用方的**表单字段名**覆盖。
   *
   * 【为什么需要它】同一个候选（`supplierName`）在两个页面里叫法不同：
   * 法务工作台的表单叫「供应商」，合同台账的表单叫「对方单位」——
   * 后者更准，因为总包合同的对方是建设单位（甲方），不是供应商。
   *
   * 但**面板的文字必须与它填进去的那个框同名**。不一样就会出现
   * "面板说填了「供应商」，表单上却找不到「供应商」这个框"——
   * 与从前那条"输入框有值、文案说未预填"是同一类错位。
   * 默认值保持原有措辞，法务工作台与它的回归测试都不受影响。
   */
  labelOverrides?: Partial<Record<keyof ExtractedCandidates, string>>;
  /**
   * 「约定品类」在调用方表单里是什么控件。
   *
   * * `'text'`（默认）：顿号分隔的普通输入框——法务工作台用它，
   *   提交时后端收字符串再 split。
   * * `'tags'`：`Select mode="tags"`，值必须是**数组**——合同台账用它。
   *
   * 【为什么必须区分】预填时把数组 `join('、')` 成字符串塞进 tags-Select，
   * 提交上去就是一个字符串，而后端 zod 要的是数组，直接 400。
   * 这个 bug 只有点保存才暴露：面板显示正常、输入框也显示正常，
   * 错的是"值在表单里的形状"。实测就是这么撞上的。
   */
  categoriesMode?: 'text' | 'tags';
}

/**
 * 组件的对外句柄。
 *
 * 【为什么需要它】附件抽屉里的「重新提取」会重新解析一份**已经上传过**的附件。
 * 从前那一步会顺带刷新识别面板，否则用户点了「重新提取」看不到任何变化——
 * 界面纹丝不动，而数据已经变了。面板现在住在本组件里，所以要把这个能力
 * 开放出来，而不是让调用方够不着、把"面板不刷新"留成静默的不一致。
 */
export interface ContractAttachmentIntakeHandle {
  refreshFrom: (attachment: ContractAttachment) => void;
}

export const ContractAttachmentIntake = forwardRef<
  ContractAttachmentIntakeHandle,
  ContractAttachmentIntakeProps
>(function ContractAttachmentIntake(
  {
    form,
    contractId,
    onAttachmentIdsChange,
    onChanged,
    showList = true,
    disabled = false,
    hint,
    labelOverrides,
    categoriesMode = 'text',
  },
  ref,
): React.ReactElement {
  const isNew = !contractId;
  const [uploaded, setUploaded] = useState<ContractAttachment[]>([]);
  const [tempIds, setTempIds] = useState<string[]>([]);
  /** 识别到的候选，用于展示"依据"供人工核对；`applied` 记录哪些已写进表单 */
  const [prefill, setPrefill] = useState<{
    candidates: ExtractedCandidates;
    applied: Record<string, unknown>;
  } | null>(null);

  const setTemp = (next: string[]): void => {
    setTempIds(next);
    onAttachmentIdsChange?.(next);
  };

  /**
   * 上传完成 / 外部重新解析后：**新建场景**才预填。
   *
   * 【为什么用 isFieldTouched 而不是 initialValues】
   * initialValues 只在首次挂载生效，表单已经打开的情况下改它没有任何效果。
   *
   * 【为什么上传与「重新提取」共用这一段】两者的产出完全一样（一份新的
   * candidates），只是触发时机不同。分开写就会出现"上传时预填、重新提取时不预填"
   * 这类只有对比两条路径才看得出来的差异。
   */
  const applyOrRefresh = (att: ContractAttachment): void => {
    setUploaded((prev) => [att, ...prev]);
    if (att.status === 'TEMP') setTemp([...tempIds, att.id]);
    onChanged?.(att);

    const c = att.extractedFields as ExtractedCandidates | null;
    if (!c) return;

    /* 已有合同：只刷新面板，不往表单里写值 */
    if (!isNew) {
      setPrefill({ candidates: c, applied: {} });
      return;
    }

    const usable = (
      f: { value: unknown; confidence: number; conflicts?: string[] } | null,
    ): boolean =>
      Boolean(
        f && f.value !== null && f.value !== undefined && f.confidence >= 60 && !f.conflicts?.length,
      );

    const patch: Record<string, unknown> = {};
    const meta: Record<string, unknown> = {};

    if (usable(c.amountCents) && !form.isFieldTouched('amountYuan')) {
      // 后端已算好整数分，这里只做展示换算，提交时回传的仍是 amountCents
      patch['amountYuan'] = Number(c.amountCents!.value) / 100;
      meta['amountCents'] = c.amountCents;
    }
    if (usable(c.supplierName) && !form.isFieldTouched('supplierName')) {
      patch['supplierName'] = c.supplierName!.value;
      meta['supplierName'] = c.supplierName;
    }
    if (usable(c.signDate) && !form.isFieldTouched('signDate')) {
      patch['signDate'] = dayjs(String(c.signDate!.value));
      meta['signDate'] = c.signDate;
    }
    if (usable(c.supplierTaxNo) && !form.isFieldTouched('supplierTaxNo')) {
      patch['supplierTaxNo'] = c.supplierTaxNo!.value;
      meta['supplierTaxNo'] = c.supplierTaxNo;
    }
    if (usable(c.categories) && !form.isFieldTouched('categories')) {
      const list = c.categories!.value as string[];
      // 按调用方的控件形态给值：tags-Select 收数组，普通输入框收顿号分隔的字符串
      patch['categories'] = categoriesMode === 'tags' ? list : list.join('、');
      meta['categories'] = c.categories;
    }
    /* contractTitle 与 projectName 刻意不自动填，见文件头第 3 条 */

    if (Object.keys(patch).length > 0) form.setFieldsValue(patch);
    // 不管有没有预填，都把候选展示出来（含没被采用的），否则用户不知道系统看出过什么
    setPrefill({ candidates: c, applied: meta });
  };

  useImperativeHandle(ref, () => ({ refreshFrom: applyOrRefresh }));

  /** 一键采用某个候选（低置信度/带冲突的也能用——由人判断） */
  const adopt = (row: CandidateRow): void => {
    if (!prefill) return;
    const f = prefill.candidates[row.key];
    if (!f || f.value === null || f.value === undefined) return;

    let patch: Record<string, unknown>;
    if (row.key === 'amountCents') patch = { amountYuan: Number(f.value) / 100 };
    else if (row.key === 'signDate') patch = { signDate: dayjs(String(f.value)) };
    else if (row.key === 'categories') {
      const list = f.value as string[];
      patch = { categories: categoriesMode === 'tags' ? list : list.join('、') };
    }
    // 两个名称候选共用表单上的「合同名称」字段，由人点选
    else if (row.key === 'contractTitle' || row.key === 'projectName') {
      patch = { name: String(f.value) };
    } else patch = { [row.key]: f.value };

    form.setFieldsValue(patch);
    setPrefill({ ...prefill, applied: { ...prefill.applied, [row.key]: f } });
  };

  return (
    <>
      <FileUploader
        disabled={disabled}
        upload={(file, onProgress) => contractApi.uploadAttachment(file, contractId ?? undefined, onProgress)}
        onUploaded={(r) => applyOrRefresh(r as ContractAttachment)}
      />
      {hint ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Typography.Text>
      ) : null}

      {/* —— 识别结果面板：必须逐项核对，不能默默填完就当数 —— */}
      {prefill
        ? (() => {
            const c = prefill.candidates;
            const applied = prefill.applied;

            const displayValue = (v: unknown, key: string): string => {
              if (typeof v === 'number') {
                return key === 'amountCents' ? `${(v / 100).toFixed(2)} 元` : String(v);
              }
              if (Array.isArray(v)) return v.join('、');
              return String(v);
            };

            return (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12, marginBottom: 12 }}
                message="以下字段由附件自动识别，已预填（或可点「采用」填入）——请逐项核对后保存"
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {CANDIDATE_ROWS.map((rawRow) => {
                      const row = labelOverrides?.[rawRow.key]
                        ? { ...rawRow, label: labelOverrides[rawRow.key]! }
                        : rawRow;
                      const f = c[row.key] as
                        | {
                            value: unknown;
                            confidence: number;
                            evidence: string;
                            rule: string;
                            conflicts?: string[];
                          }
                        | null
                        | undefined;
                      // 老附件（本次新增字段之前入库的）jsonb 里没有这个键：
                      // 与"识别过但没值"是两回事——前者不显示，后者要如实说没识别到
                      if (f === undefined) return null;
                      const hasValue = f !== null && f.value !== null && f.value !== undefined;
                      return (
                        <span key={String(row.key)} style={{ fontSize: 12 }}>
                          <strong>{row.label}</strong>：
                          {hasValue ? displayValue(f!.value, String(row.key)) : '未识别到'}
                          {f ? ` · 置信度 ${f.confidence}` : ''}
                          {applied[row.key] ? ' · 已预填' : hasValue ? ' · 未预填' : ''}
                          {hasValue && !applied[row.key] ? (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: '0 4px', height: 'auto' }}
                              onClick={() => adopt(row)}
                            >
                              采用
                            </Button>
                          ) : null}
                          {f?.conflicts?.length ? ` · ⚠ ${f.conflicts.join('；')}` : null}
                          <br />
                          {f ? (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              依据：{f.evidence}（规则 {f.rule}）
                              {row.hint ? ` · ${row.hint}` : ''}
                            </Typography.Text>
                          ) : null}
                        </span>
                      );
                    })}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      识别是启发式的，金额与对方单位尤其容易受版式影响。
                      <strong>金额识别错误会直接影响后续「超出合同额度」的判断。</strong>
                      {isNew ? null : '（当前是往已有合同上传附件，识别结果不会改写已保存的字段——要采用请点上面的按钮。）'}
                    </Typography.Text>
                  </Space>
                }
              />
            );
          })()
        : null}

      {/* —— 本次上传的附件 —— */}
      {showList && uploaded.length > 0 ? (
        <Table<ContractAttachment>
          size="small"
          rowKey="id"
          pagination={false}
          style={{ marginTop: 12, marginBottom: 12 }}
          dataSource={uploaded}
          columns={[
            { title: '文件', dataIndex: 'fileName', ellipsis: true },
            {
              title: '提取状态',
              width: 250,
              render: (_v, r) => (
                <AttachmentStatusTag
                  status={r.extractStatus}
                  impl={r.extractImpl}
                  mode={r.extractMode}
                  message={r.extractMessage}
                  missing={r.extractMissing}
                />
              ),
            },
            {
              title: '操作',
              width: 80,
              render: (_v, r) =>
                r.status === 'TEMP' ? (
                  <Popconfirm
                    title="移除这份附件？"
                    description="文件本身不会被删除（存储按内容寻址，别处可能还在用），只是不再绑定到这张合同。"
                    okText="移除"
                    cancelText="取消"
                    onConfirm={() => {
                      setUploaded((prev) => prev.filter((x) => x.id !== r.id));
                      setTemp(tempIds.filter((id) => id !== r.id));
                    }}
                  >
                    <Button size="small" type="link" danger disabled={disabled}>
                      移除
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      ) : null}
    </>
  );
});

export default ContractAttachmentIntake;
